import { Theme } from '@emotion/react';
import { rem } from './rem';

export enum TextVariant {
  DisplayLg = 'type-display-lg',
  Header5xl = 'type-header-5xl',
  Header4xl = 'type-header-4xl',
  Header3xl = 'type-header-3xl',
  Header2xl = 'type-header-2xl',
  HeaderXl = 'type-header-xl',
  HeaderLg = 'type-header-lg',
  HeaderMd = 'type-header-md',
  HeaderSm = 'type-header-sm',
  HeaderXs = 'type-header-xs',
  BodyXl = 'type-body-xl',
  BodyXlBold = 'type-body-xl-bold',
  BodyLg = 'type-body-lg',
  BodyMd = 'type-body-md',
  BodyMdBold = 'type-body-md-bold',
  BodySm = 'type-body-sm',
  BodySmBold = 'type-body-sm-bold',
  BodyXs = 'type-body-xs',
  Caption = 'type-caption',
  StatusTag = 'type-statusTag',
  Button = 'type-button',
  ToggleButton = 'type-toggleButton',
}

export enum FontWeight {
  Hairline = 100,
  Thin = 200,
  Light = 300,
  Normal = 400,
  Medium = 500,
  SemiBold = 600,
  Bold = 700,
  ExtraBold = 800,
  Black = 900,
}

export enum FontFamily {
  Body = 'body',
  Heading = 'heading',
  Mono = 'mono',
}

const typography: Omit<Theme, 'colors'> = {
  fontSizes: [
    '0.75rem', // 12px
    '0.875rem', // 14px
    '1rem', // 16px
    '1.25rem', // 20px
    '1.5rem', //24px
    '1.75rem', //28px
    '2rem', // 32px
    '2.5rem', // 40px,
    '3rem', // 48px,
    '4rem', // 64px,
  ],
  fonts: {
    [FontFamily.Heading]:
      'ABC Diatype Rounded, "Helvetica Neue", Helvetica, Arial, sans-serif',
    [FontFamily.Body]:
      'Inclusive Sans, "Helvetica Neue", Helvetica, Arial, sans-serif',
  },
  text: {
    [TextVariant.DisplayLg]: {
      fontFamily: FontFamily.Heading,
      fontWeight: FontWeight.Normal,
      fontSize: [rem(48), rem(96)],
      lineHeight: [rem(56), rem(104)],
      overflowWrap: 'break-word',
    },
    [TextVariant.Header5xl]: {
      fontFamily: FontFamily.Heading,
      fontWeight: FontWeight.Normal,
      fontSize: [rem(48), rem(80)],
      lineHeight: [rem(56), rem(96)],
      letterSpacing: rem(1.2),
      overflowWrap: 'break-word',
    },
    [TextVariant.Header4xl]: {
      fontFamily: FontFamily.Heading,
      fontWeight: FontWeight.Normal,
      fontSize: [rem(40), rem(64)],
      lineHeight: [rem(40), rem(72)],
      letterSpacing: rem(1.2),
      overflowWrap: 'break-word',
    },
    [TextVariant.Header3xl]: {
      fontFamily: FontFamily.Heading,
      fontWeight: FontWeight.Normal,
      fontSize: [rem(32), rem(48)],
      lineHeight: [rem(36), rem(56)],
      letterSpacing: rem(1.2),
      overflowWrap: 'break-word',
    },
    [TextVariant.Header2xl]: {
      fontFamily: FontFamily.Heading,
      fontWeight: FontWeight.Normal,
      fontSize: [rem(24), rem(40)],
      lineHeight: [rem(32), rem(48)],
      letterSpacing: rem(1.2),
      overflowWrap: 'break-word',
    },
    [TextVariant.HeaderXl]: {
      fontFamily: FontFamily.Heading,
      fontWeight: FontWeight.Medium,
      fontSize: [rem(22), rem(32)],
      lineHeight: [rem(28), rem(40)],
      letterSpacing: rem(1.2),
      overflowWrap: 'break-word',
    },
    [TextVariant.HeaderLg]: {
      fontFamily: FontFamily.Heading,
      fontWeight: FontWeight.Medium,
      fontSize: rem(24),
      lineHeight: rem(32),
      letterSpacing: rem(0),
      overflowWrap: 'break-word',
    },
    [TextVariant.HeaderMd]: {
      fontFamily: FontFamily.Heading,
      fontSize: rem(20),
      fontWeight: FontWeight.Medium,
      lineHeight: rem(28),
      letterSpacing: rem(0),
      overflowWrap: 'break-word',
    },
    [TextVariant.HeaderSm]: {
      fontFamily: FontFamily.Heading,
      fontSize: rem(16),
      fontWeight: FontWeight.Medium,
      lineHeight: rem(24),
      letterSpacing: rem(0),
      overflowWrap: 'break-word',
    },
    [TextVariant.HeaderXs]: {
      fontFamily: FontFamily.Heading,
      fontSize: rem(14),
      fontWeight: FontWeight.Medium,
      lineHeight: rem(20),
      letterSpacing: rem(0),
      overflowWrap: 'break-word',
    },
    [TextVariant.BodyXl]: {
      fontFamily: FontFamily.Body,
      fontWeight: FontWeight.Normal,
      fontSize: [rem(20), rem(24)],
      lineHeight: [rem(28), rem(36)],
      letterSpacing: rem(0),
      overflowWrap: 'break-word',
    },
    [TextVariant.BodyXlBold]: {
      fontFamily: FontFamily.Body,
      fontStyle: 'normal',
      fontWeight: FontWeight.Medium,
      fontSize: [rem(20), rem(24)],
      lineHeight: [rem(28), rem(36)],
      letterSpacing: rem(0),
      overflowWrap: 'break-word',
    },
    [TextVariant.BodyLg]: {
      fontFamily: FontFamily.Body,
      fontWeight: FontWeight.Normal,
      fontSize: [rem(18), rem(20)],
      lineHeight: [rem(28), rem(32)],
      letterSpacing: rem(0),
      overflowWrap: 'break-word',
    },
    [TextVariant.BodyMd]: {
      fontFamily: FontFamily.Body,
      fontWeight: FontWeight.Normal,
      fontSize: rem(16),
      lineHeight: rem(24),
      letterSpacing: rem(0),
      overflowWrap: 'break-word',
    },
    [TextVariant.BodyMdBold]: {
      fontFamily: FontFamily.Body,
      fontWeight: FontWeight.Medium,
      fontSize: rem(16),
      lineHeight: rem(24),
      letterSpacing: rem(0),
      overflowWrap: 'break-word',
    },
    [TextVariant.BodySm]: {
      fontFamily: FontFamily.Body,
      fontWeight: FontWeight.Normal,
      fontSize: rem(14),
      lineHeight: rem(20),
      letterSpacing: rem(0),
      overflowWrap: 'break-word',
    },
    [TextVariant.BodySmBold]: {
      fontFamily: FontFamily.Body,
      fontWeight: FontWeight.Medium,
      fontSize: rem(14),
      lineHeight: rem(20),
      letterSpacing: rem(0),
      overflowWrap: 'break-word',
    },
    [TextVariant.BodyXs]: {
      fontFamily: FontFamily.Body,
      fontWeight: FontWeight.Normal,
      fontSize: rem(12),
      lineHeight: rem(18),
      letterSpacing: rem(0),
      overflowWrap: 'break-word',
    },
    [TextVariant.Caption]: {
      fontFamily: FontFamily.Body,
      fontWeight: FontWeight.Normal,
      fontSize: rem(14),
      lineHeight: rem(20),
      letterSpacing: rem(0),
      overflowWrap: 'break-word',
    },
    [TextVariant.StatusTag]: {
      fontFamily: FontFamily.Body,
      fontWeight: FontWeight.Normal,
      fontSize: rem(12),
      lineHeight: rem(14),
      letterSpacing: rem(0),
      overflowWrap: 'break-word',
    },
    [TextVariant.Button]: {
      fontFamily: FontFamily.Heading,
      fontWeight: FontWeight.Normal,
      fontSize: [rem(12), rem(16)],
      lineHeight: [1.33, 1],
      overflowWrap: 'break-word',
    },
    /* type-toggleButton */
    [TextVariant.ToggleButton]: {
      fontFamily: FontFamily.Heading,
      fontWeight: FontWeight.Normal,
      fontSize: rem(14),
      lineHeight: rem(14),
      overflowWrap: 'break-word',
    },
  },
};

export default typography;
